// Embeddable public contact form (form body only — the host page owns all
// surrounding chrome). Posts to /api/public/contact with the PublicContactZ
// payload: hard RGPD acknowledgement plus the optional, unchecked-by-default
// marketing email opt-in whose wording is versioned server-side as
// contact.form.marketingOptIn@v1 — the strings rendered here ARE that
// reference wording (src/messages/*/contact.json).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { AlertCircle, Loader2, Send } from 'lucide-react';
import { EmbedConfirmation } from './confirmation';
import { Turnstile } from '../../src/components/security/TurnstileLite.client';
import { PHONE_RE } from '../../src/lib/format/phone';
import type { Locale } from '../../src/i18n/config';

interface PublicClinic {
  id: string;
  slug: string | null;
  display_name: string;
}

export interface ContactSuccessDetail {
  clinicId: string;
  clinicSlug: string | null;
  subject?: string;
  email: string;
  phone?: string;
  marketingOptIn: boolean;
}

export interface ContactFormProps {
  baseUrl: string;
  locale: Locale;
  clinics: PublicClinic[];
  /** uuid or slug preselect (element attribute or host ?clinica=/?clinic=). */
  preselectClinic?: string;
  /** Attribute override; falls back to the site's configured key. */
  turnstileSiteKey?: string;
  theme: string;
  onStart?: () => void;
  onSuccess?: (detail: ContactSuccessDetail) => void;
  onError?: (code: string) => void;
}

type Status = 'idle' | 'sending' | 'success' | 'error';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function ContactFormEmbed({
  baseUrl,
  locale,
  clinics,
  preselectClinic,
  turnstileSiteKey,
  theme,
  onStart,
  onSuccess,
  onError,
}: ContactFormProps) {
  const t = useTranslations('contact.form');
  const tv = useTranslations('contact.validation');
  const te = useTranslations('contact.errors');

  const preselected = useMemo(() => {
    if (!preselectClinic) return undefined;
    return clinics.find((c) =>
      UUID_RE.test(preselectClinic)
        ? c.id.toLowerCase() === preselectClinic.toLowerCase()
        : c.slug === preselectClinic,
    );
  }, [clinics, preselectClinic]);

  const [clinicId, setClinicId] = useState<string>(
    preselected?.id ?? (clinics.length === 1 ? clinics[0]!.id : ''),
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const startedRef = useRef(false);
  // One idempotency key per form-fill: retries of a failed submit reuse it
  // (safe replay), a fresh fill after success gets a new one.
  const idemKeyRef = useRef<string>(crypto.randomUUID());

  const captchaRequired = Boolean(turnstileSiteKey);

  const onFirstInteraction = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    onStart?.();
  }, [onStart]);

  useEffect(() => {
    if (preselected) setClinicId(preselected.id);
  }, [preselected]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (name.trim().length < 2) next.name = tv('nameRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = tv('emailInvalid');
    if (!PHONE_RE.test(phone.trim())) next.phone = tv('phoneRequired');
    if (!clinicId) next.clinic = tv('clinicRequired');
    if (message.trim().length < 2) next.message = tv('messageRequired');
    if (!consent) next.consent = tv('consentRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!validate()) return;
    if (captchaRequired && !turnstileToken) {
      setStatus('error');
      setErrorCode('captchaPending');
      onError?.('captcha_pending');
      return;
    }
    setStatus('sending');
    setErrorCode(null);
    try {
      const res = await fetch(`${baseUrl}/api/public/contact`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          clinicId,
          locale,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          subject: subject.trim() || undefined,
          body: message.trim(),
          source: 'widget:embed',
          consent: { rgpd: true, marketing: { email: marketingOptIn } },
          idempotencyKey: idemKeyRef.current,
          ...(turnstileToken ? { turnstileToken } : {}),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        const code =
          res.status === 429 ? 'rateLimited' : body.error === 'captcha_failed' ? 'captchaFailed' : 'generic';
        setStatus('error');
        setErrorCode(code);
        onError?.(body.error ?? `http_${res.status}`);
        // Turnstile tokens are single-use; remount so the user can retry.
        setTurnstileToken('');
        setTurnstileNonce((n) => n + 1);
        return;
      }
      const clinic = clinics.find((c) => c.id === clinicId);
      setStatus('success');
      onSuccess?.({
        clinicId,
        clinicSlug: clinic?.slug ?? null,
        subject: subject.trim() || undefined,
        email: email.trim(),
        phone: phone.trim(),
        marketingOptIn,
      });
      idemKeyRef.current = crypto.randomUUID();
    } catch {
      setStatus('error');
      setErrorCode('generic');
      onError?.('network');
      setTurnstileToken('');
      setTurnstileNonce((n) => n + 1);
    }
  };

  if (status === 'success') {
    return <EmbedConfirmation theme={theme} title={t('successTitle')} body={t('successBody')} />;
  }

  const showClinicSelect = !preselected && clinics.length > 1;

  return (
    <div className="booking-widget booking-widget--bare" data-bw-theme={theme}>
      <form
        className="patient-form embed-card"
        noValidate
        onSubmit={submit}
        onFocus={onFirstInteraction}
      >
        {status === 'error' && (
          <div className="booking-error booking-error--icon" role="alert">
            <AlertCircle aria-hidden />
            <div>
              <p>{t('errorTitle')}</p>
              <p>{errorCode ? te(errorCode) : te('generic')}</p>
            </div>
          </div>
        )}

        <div className="patient-form-row">
          <label className="patient-form-field">
            <span className="patient-form-label">{t('name')} *</span>
            <input
              required
              autoComplete="name"
              placeholder={t('namePlaceholder')}
              className={`patient-form-input${errors.name ? ' is-invalid' : ''}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {errors.name && <p className="patient-form-error">{errors.name}</p>}
          </label>
          <label className="patient-form-field">
            <span className="patient-form-label">{t('email')} *</span>
            <input
              required
              type="email"
              autoComplete="email"
              placeholder={t('emailPlaceholder')}
              className={`patient-form-input${errors.email ? ' is-invalid' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && <p className="patient-form-error">{errors.email}</p>}
          </label>
        </div>

        <div className="patient-form-row">
          <label className="patient-form-field">
            <span className="patient-form-label">{t('phone')} *</span>
            <input
              required
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder={t('phonePlaceholder')}
              className={`patient-form-input${errors.phone ? ' is-invalid' : ''}`}
              aria-invalid={Boolean(errors.phone)}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {errors.phone && <p className="patient-form-error">{errors.phone}</p>}
          </label>
          {showClinicSelect ? (
            <label className="patient-form-field">
              <span className="patient-form-label">{t('clinic')} *</span>
              <select
                required
                className={`patient-form-input${errors.clinic ? ' is-invalid' : ''}`}
                value={clinicId}
                onChange={(e) => setClinicId(e.target.value)}
              >
                <option value="" disabled>
                  {t('clinicPlaceholder')}
                </option>
                {clinics.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
              {errors.clinic && <p className="patient-form-error">{errors.clinic}</p>}
            </label>
          ) : (
            <label className="patient-form-field">
              <span className="patient-form-label">{t('subject')}</span>
              <input
                placeholder={t('subjectPlaceholder')}
                className="patient-form-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>
          )}
        </div>

        {showClinicSelect && (
          <label className="patient-form-field">
            <span className="patient-form-label">{t('subject')}</span>
            <input
              placeholder={t('subjectPlaceholder')}
              className="patient-form-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </label>
        )}

        <label className="patient-form-field">
          <span className="patient-form-label">{t('message')} *</span>
          <textarea
            required
            rows={5}
            placeholder={t('messagePlaceholder')}
            className={`patient-form-textarea${errors.message ? ' is-invalid' : ''}`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          {errors.message && <p className="patient-form-error">{errors.message}</p>}
        </label>

        <div className={`checkbox-container${errors.consent ? ' is-invalid' : ''}`}>
          <input
            id="contact-consent"
            type="checkbox"
            aria-invalid={Boolean(errors.consent)}
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <div>
            <label htmlFor="contact-consent" className="cursor-pointer text-sm">
              {t('consent')}
            </label>
            {errors.consent && <p className="patient-form-error">{errors.consent}</p>}
          </div>
        </div>

        <div className="checkbox-container">
          <input
            id="contact-marketing"
            type="checkbox"
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
          />
          <label htmlFor="contact-marketing" className="cursor-pointer text-sm">
            {t('marketingOptIn')}
          </label>
        </div>

        {turnstileSiteKey && (
          <Turnstile
            key={turnstileNonce}
            siteKey={turnstileSiteKey}
            locale={locale}
            onToken={setTurnstileToken}
          />
        )}

        <button
          type="submit"
          disabled={status === 'sending' || (captchaRequired && !turnstileToken)}
          className="cta-primary cta-primary--fit"
        >
          {status === 'sending' ? (
            <Loader2 className="cta-primary-icon animate-spin" aria-hidden />
          ) : (
            <Send className="cta-primary-icon" aria-hidden />
          )}
          {status === 'sending' ? t('sending') : t('submit')}
        </button>
      </form>
    </div>
  );
}
